import { EventsService } from "@/api/events";
import { handleApiError } from "@/utils/errorHandler";
import { getCurrentClientData } from "./authServices";

export interface SubscriptionStatus {
  isPro: boolean;
  planId: string;
  planName: string;
  nextBillingDate: string | null;
  features: string[];
}

const PRO_FEATURES = [
  "Multiple ticket types",
  "Event analytics",
  "Custom registration fields",
];

const FREE_FEATURES = [
  "Basic event creation",
  "Single ticket type",
  "Limited registrations",
];

const getFallbackSubscription = (): SubscriptionStatus => {
  const isPro = localStorage.getItem("isPro") === "true";

  return {
    isPro,
    planId: isPro ? "pro" : "free",
    planName: isPro ? "Pro Plan" : "Free Plan",
    nextBillingDate: isPro ? "2025-05-24" : null,
    features: isPro ? PRO_FEATURES : FREE_FEATURES,
  };
};

const mapSubscriptionResponse = (response: any): SubscriptionStatus => {
  const accountData = response?.body?.data?.account_type;

  if (!accountData) {
    return getFallbackSubscription();
  }

  const planId = accountData.account_type === "pro" ? "pro" : "free";
  const isPro = planId === "pro" && accountData.status === "active";

  return {
    isPro,
    planId,
    planName: planId === "pro" ? "Pro Plan" : "Free Plan",
    nextBillingDate: accountData.valid_until ?? null,
    features: isPro ? PRO_FEATURES : FREE_FEATURES,
  };
};

export const getCurrentSubscription = async (): Promise<SubscriptionStatus> => {
  const { token } = getCurrentClientData();

  if (!token) {
    return getFallbackSubscription();
  }

  try {
    const response: any = await EventsService.subscriptionType(token);

    if (response?.statusCode === 200) {
      return mapSubscriptionResponse(response);
    }

    return getFallbackSubscription();
  } catch (error: any) {
    console.warn("Failed to fetch subscription data", error);
    return getFallbackSubscription();
  }
};

export const upgradeToPro = async (): Promise<boolean> => {
  try {
    localStorage.setItem("isPro", "true");
    return true;
  } catch (error: any) {
    return handleApiError(error, "Error upgrading subscription");
  }
};

export const cancelSubscription = async (): Promise<boolean> => {
  try {
    localStorage.setItem("isPro", "false");
    return true;
  } catch (error: any) {
    return handleApiError(error, "Error cancelling subscription");
  }
};
